import type { Editor } from '@tiptap/react';
import type { Node } from '@tiptap/pm/model';
import type { UIMessage } from 'ai';
import { isEditorWriteToolType } from '@flintmint/core-shared/utils';
import type { GenerationUsageMessageMetadata } from '@flintmint/core-shared/message-metadata';
import { getDisplayText, getToolParts } from '@flintmint/web-shared/utils/llm-message-utils';

import { capture } from '@/analytics';
import { getEditorHTML, isAiToolkitReady, isEditorAlive } from '@/utils/editor-utils';

import {
  clearAgentRunEditSnapshot,
  clearAgentRunPendingReviewCommit,
  clearRunScrollCleanup,
  getAgentRunDurableEdit,
  getAgentRunEditScope,
  getAgentRunPendingReviewCommit,
  getAgentRunRevertData,
  getAgentRunStartedFromEmptyEditor,
  getAgentRunState,
  getAgentRunStreamingEnabled,
  getCurrentAgentRunId,
  hasAgentRunClaimedTerminalization,
  isAgentRunAwaitingPostEditSummary,
  isCurrentAgentRunSnapshot,
  publishAgentRunEvent,
  releaseAgentRunTerminalizationClaim,
  setAgentRunDurableEdit,
  setAgentRunPendingReviewCommit,
  setAgentRunRevertData,
  setAgentRunStartedFromEmptyEditor,
  tryClaimAgentRunTerminalization,
} from './agent-run-store';
import { emitAgentRunFinished } from './agent-run-finish-notifier';
import { captureNoteGeneration, captureNoteGenerationFailed } from '../note-generation-session-store';
import { buildCompletionDocumentPatch } from './completion-patch';
import { getRequestEditor, endEditorStreamingPreview } from '../request-editor';
import { type SuggestionDiffConfig } from '@/hooks/llm/editor-suggestion-config';
import { evictCachedEditor } from '@/hooks/use-block-editor/editor-cache';
import { getCurrentTurnAssistantMessages } from '@flintmint/core-shared/utils/editor-write-tool';
import { getCurrentTurnEditorWriteToolCallIds, hasSetDocumentNameInCurrentTurn } from '../current-turn-assistant-messages';
import { getTargetedEditRange, type AgentEditTarget } from '../edit-target';
import { createPendingReview, type PendingReview } from '../review/persisted-review';
import { getEditorCharCount, getAgentDocumentType, type PersistedDocument } from '../document-utils';
import type { PersistedDocumentWriter } from '../review/review-session-types';
import { deriveEditorState, isAgentRunLoading } from './agent-run-model';
import type { AiRefusal, CompletionOutcome, EditorState, PendingReviewCommit, RevertData } from './agent-run-model';
import { revertEditorToOriginalContent, startPreparedAttempt, type PreparedAgentAttempt } from './prepare-run';
import type { AgentFinishResult } from '../use-llm-agent';
import type { AutoNameDocumentParams } from '../auto-name-document';
import { isLikelyNetworkError } from '../is-likely-network-error';
import { AuthDeniedError } from '../session/agent-fetch';
import { assertDocumentFitsTextBudget, assertReviewFitsContentBudget, ContentTooLargeError } from './content-size-check';

type CompletionPlanOptions = {
  requestId: string;
  messages: UIMessage[];
  editorState?: EditorState;
  editor?: Editor | null;
  persistDocumentPatch?: PersistedDocumentWriter;
};

// What a successful completion persists: a review to commit, or nothing beyond its outcome's own
// patch.
type CompletionPlan =
  | { outcome: CompletionOutcome }
  | { outcome: 'commit_review'; pendingReview: PendingReview; persistDocumentPatch: PersistedDocumentWriter };

type CompletionOutcomeInspection =
  | {
      outcome: CompletionOutcome;
    }
  | {
      outcome: 'needs_review_resolution';
      revertData: RevertData;
    };

export type CompletionResolution =
  | {
      outcome: 'review_ready';
    }
  | {
      outcome: Exclude<CompletionOutcome, 'review_ready'>;
    };

export type RunGuard = { isStale: () => boolean };

type SuccessfulAgentRunFinalization = {
  requestId: string;
  runId: number;
  runGuard: RunGuard;
  editor: Editor | null;
  document?: PersistedDocument;
  resolution: CompletionResolution;
  awaitingPostEditSummary: boolean;
  hasEdit: boolean;
  persistCompletionPatch: () => Promise<void>;
  capture: {
    outputCharCount: number;
    promptInputCharCount: number;
    targetedEdit: boolean;
    streamingEnabled: boolean;
    usage?: GenerationUsageMessageMetadata;
  };
  onEditPhaseFinish?: (result?: AgentFinishResult) => void;
  onFinish?: (result?: AgentFinishResult) => void;
};

type PendingReviewCommitResult = { status: 'none' } | { status: 'persisted' } | { status: 'failed'; error: unknown };

export type AgentLifecycleRuntime = {
  editor: Editor | null;
  document?: PersistedDocument;
  persistDocumentPatch?: PersistedDocumentWriter;
  autoNameIfNeeded?: (params: AutoNameDocumentParams) => Promise<string | undefined>;
  resetClassificationCache?: () => void;
  onEditPhaseFinish?: (result?: AgentFinishResult) => void;
  onFinish?: (result?: AgentFinishResult) => void;
  onError?: (error: Error) => void;
};

export type AgentRunLifecycle = {
  prepareAttempt(runId: number, editTarget: AgentEditTarget, streamingEnabled: boolean): PreparedAgentAttempt | null;
  finalizeDocEdit(messages: UIMessage[]): Promise<void>;
  finalizeRun(messages: UIMessage[]): Promise<void>;
  cancel(messages?: UIMessage[]): Promise<void>;
  error(runError: Error, messages?: UIMessage[]): Promise<void>;
  refuse(refusal?: AiRefusal): Promise<void>;
};

export function createAgentRunLifecycle({
  requestId,
  resolveRuntime,
  resolveDocument,
  stop,
  markEditorWritesRefused,
}: {
  requestId: string;
  resolveRuntime: () => AgentLifecycleRuntime | undefined;
  resolveDocument: () => PersistedDocument | undefined;
  stop: () => void | Promise<void>;
  // Rewrites the chat history's outputs for the current turn's editor writes, except
  // `keptToolCallIds`, as not applied.
  markEditorWritesRefused: (keptToolCallIds: string[]) => void;
}): AgentRunLifecycle {
  const claimCurrentRunTerminalization = (source: 'finish' | 'cancel' | 'error'): number | null => {
    const runId = getCurrentAgentRunId(requestId);
    if (!runId || !tryClaimAgentRunTerminalization(requestId, runId, source)) {
      return null;
    }

    return runId;
  };

  const finalizeCommittedReviewTerminalization = async (runId: number): Promise<boolean> => {
    const runGuard = createRunGuard(requestId, runId);
    const pendingCommitResult = await waitForPendingReviewCommit(requestId, runId);
    if (runGuard.isStale()) {
      return true;
    }

    const hasPersistedReview =
      pendingCommitResult.status === 'persisted' || deriveEditorState(getAgentRunState(requestId)) === 'review';

    if (!hasPersistedReview) {
      return false;
    }

    if (deriveEditorState(getAgentRunState(requestId)) !== 'review') {
      publishAgentRunEvent(requestId, runId, { type: 'review_ready' });
    }

    publishAgentRunEvent(requestId, runId, { type: 'completed' });
    evictCachedEditor(requestId);
    return true;
  };

  const finalizeTerminalizedRun = ({
    runId,
    currentEditor,
    stopRequest = false,
    revertData,
    onTerminalizeWithoutReview,
  }: {
    runId: number;
    currentEditor?: Editor | null;
    stopRequest?: boolean;
    // What to restore when the run ends without a review; the run's revert data by default.
    revertData?: RevertData | null;
    onTerminalizeWithoutReview: () => void;
  }): Promise<void> => {
    clearRunScrollCleanup(requestId);
    if (stopRequest) {
      void stop();
    }
    endEditorStreamingPreview(requestId, currentEditor);

    const terminalizeWithoutReview = () => {
      onTerminalizeWithoutReview();
      if (currentEditor) {
        revertEditorToOriginalContent(requestId, currentEditor, revertData);
      }
    };

    if (!getPendingReviewCommit(requestId, runId)) {
      if (deriveEditorState(getAgentRunState(requestId)) === 'review') {
        publishAgentRunEvent(requestId, runId, { type: 'completed' });
        evictCachedEditor(requestId);
      } else {
        terminalizeWithoutReview();
      }
      return Promise.resolve();
    }

    return finalizeCommittedReviewTerminalization(runId).then((didFinalize) => {
      if (!didFinalize) {
        terminalizeWithoutReview();
      }
    });
  };

  const publishCancelledRun = (runId: number, messages?: UIMessage[]): void => {
    publishAgentRunEvent(requestId, runId, { type: 'cancelled' });
    captureNoteGenerationFailed({
      requestId,
      document: resolveDocument(),
      errorType: 'abort_user',
      promptInputCharCount: getLastUserPromptCharCount(messages),
    });

    const runtime = resolveRuntime();
    runtime?.resetClassificationCache?.();
  };

  const publishErroredRun = (runId: number, error: Error, messages?: UIMessage[]): void => {
    const isNetworkError = isLikelyNetworkError(error);
    publishAgentRunEvent(requestId, runId, {
      type: 'errored',
      network: isNetworkError,
    });
    captureNoteGenerationFailed({
      requestId,
      document: resolveDocument(),
      errorType: isNetworkError ? 'network' : 'unknown',
      promptInputCharCount: getLastUserPromptCharCount(messages),
    });
    resolveRuntime()?.onError?.(error);
  };

  // A request refused by the auth gate is not a failed generation: the session handles the denial
  // (agreements screen or sign-out), so the run ends as cancelled with no failure telemetry and no
  // error callback.
  const publishAuthDeniedRun = (runId: number): void => {
    publishAgentRunEvent(requestId, runId, { type: 'cancelled' });
  };

  // A request refused for AI credits is an expected billing state, not a failed generation, so the
  // run ends with no failure telemetry and no error callback: cancelled carrying the refusal when the
  // user started the request, completed like any finished turn when a continuation was refused.
  const publishRefusedRun = (runId: number, refusal?: AiRefusal): void => {
    if (refusal) {
      publishAgentRunEvent(requestId, runId, { type: 'cancelled', refusal });
      return;
    }
    publishAgentRunEvent(requestId, runId, { type: 'completed' });
    evictCachedEditor(requestId);
  };

  // An edit too large to save is refused on purpose, so it is not logged as an error or passed to
  // the error callback; the notifications show the content size limit toast for it.
  const publishContentTooLargeRun = (runId: number, messages?: UIMessage[]): void => {
    publishAgentRunEvent(requestId, runId, { type: 'errored', network: false, reason: 'content_too_large' });
    captureNoteGenerationFailed({
      requestId,
      document: resolveDocument(),
      errorType: 'content_too_large',
      promptInputCharCount: getLastUserPromptCharCount(messages),
    });
  };

  // Plans the run's end-of-run persist synchronously, inside Chat.onFinish: the AI SDK checks
  // `sendAutomaticallyWhen` right after onFinish returns, so a size refusal must claim
  // terminalization before then for the session to block the automatic continuation. Returns null
  // when the edit is refused.
  const planOrRefuseCompletion = (
    runId: number,
    messages: UIMessage[],
    options: Pick<CompletionPlanOptions, 'editorState' | 'editor' | 'persistDocumentPatch'>
  ): CompletionPlan | null => {
    try {
      return planCompletion({ requestId, messages, ...options });
    } catch (error) {
      if (!(error instanceof ContentTooLargeError)) throw error;
      refuseOversizedRun(runId, messages);
      return null;
    }
  };

  // Ends a run whose edit failed the end-of-run size check, which runs before the edit is
  // persisted. The editor goes back to the pre-run content, or to the content an earlier edit
  // phase of this run persisted, and the chat history stops claiming the refused writes applied.
  const refuseOversizedRun = (runId: number, messages: UIMessage[]): void => {
    if (!tryClaimAgentRunTerminalization(requestId, runId, 'error')) return;

    const durableEdit = getAgentRunDurableEdit(requestId);
    markEditorWritesRefused(durableEdit?.toolCallIds ?? []);
    void finalizeTerminalizedRun({
      runId,
      currentEditor: getRequestEditor(requestId),
      revertData:
        getAgentRunRevertData(requestId) ??
        (durableEdit ? { beforeDoc: durableEdit.doc, restoreTarget: { kind: 'document' } } : null),
      onTerminalizeWithoutReview: () => publishContentTooLargeRun(runId, messages),
    });
  };

  // The current run when it is still loading and no terminal has claimed it.
  const getUnclaimedRunId = (): number | null => {
    const runId = getCurrentAgentRunId(requestId);
    const run = getAgentRunState(requestId);
    return runId && isAgentRunLoading(run) && !run?.terminalizationSource ? runId : null;
  };

  const prepareAttempt = (runId: number, editTarget: AgentEditTarget, streamingEnabled: boolean): PreparedAgentAttempt | null => {
    const editor = resolveRuntime()?.editor;

    if (!isAiToolkitReady(editor)) {
      console.error('Editor not available or destroyed, likely unmounted');
      return null;
    }

    return startPreparedAttempt({ requestId, runId, editor, editTarget, streamingEnabled });
  };

  const finalizeRun = (messages: UIMessage[]): Promise<void> => {
    const runId = getUnclaimedRunId();
    if (!runId) {
      return Promise.resolve();
    }

    const runtime = resolveRuntime();
    const plan = planOrRefuseCompletion(runId, messages, {
      editorState: deriveEditorState(getAgentRunState(requestId)),
      editor: getRequestEditor(requestId),
      persistDocumentPatch: runtime?.persistDocumentPatch,
    });
    if (!plan || !tryClaimAgentRunTerminalization(requestId, runId, 'finish')) {
      return Promise.resolve();
    }

    return (async () => {
      clearRunScrollCleanup(requestId);
      try {
        await finalizeSuccessfulChatRun({
          requestId,
          runId,
          messages,
          plan,
          runtime: runtime && { ...runtime, document: resolveDocument() },
        });
      } catch (error) {
        releaseAgentRunTerminalizationClaim(requestId, runId, 'finish');
        throw error;
      }
    })();
  };

  const finalizeDocEdit = (messages: UIMessage[]): Promise<void> => {
    const runId = getCurrentAgentRunId(requestId);
    const finishedEditor = getRequestEditor(requestId);
    if (!runId || hasAgentRunClaimedTerminalization(requestId) || !finishedEditor) {
      return Promise.resolve();
    }

    const runtime = resolveRuntime();
    const plan = planOrRefuseCompletion(runId, messages, {
      editor: finishedEditor,
      persistDocumentPatch: runtime?.persistDocumentPatch,
    });
    if (!plan) {
      return Promise.resolve();
    }

    return finalizeSuccessfulAgentEditPhase({
      requestId,
      runId,
      messages,
      plan,
      finishedEditor,
      document: resolveDocument(),
      persistDocumentPatch: runtime?.persistDocumentPatch,
      onEditPhaseFinish: runtime?.onEditPhaseFinish,
    }).then(() => undefined);
  };

  const cancel = (messages?: UIMessage[]): Promise<void> => {
    const runId = claimCurrentRunTerminalization('cancel');
    if (!runId) {
      return Promise.resolve();
    }

    const currentEditor = getRequestEditor(requestId);

    return finalizeTerminalizedRun({
      runId,
      currentEditor,
      stopRequest: true,
      onTerminalizeWithoutReview: () => publishCancelledRun(runId, messages),
    });
  };

  const error = (runError: Error, messages?: UIMessage[]): Promise<void> => {
    const runId = claimCurrentRunTerminalization('error');
    if (!runId) {
      return Promise.resolve();
    }

    const currentEditor = getRequestEditor(requestId);

    return finalizeTerminalizedRun({
      runId,
      currentEditor,
      onTerminalizeWithoutReview: () =>
        runError instanceof AuthDeniedError ? publishAuthDeniedRun(runId) : publishErroredRun(runId, runError, messages),
    });
  };

  // Without `refusal` (an automatic continuation was refused), the run ends like a finished turn: the
  // user already has the turn's response, and only their own next send reports the refusal.
  const refuse = (refusal?: AiRefusal): Promise<void> => {
    const runId = claimCurrentRunTerminalization('error');
    if (!runId) {
      return Promise.resolve();
    }

    // Only a request the user started counts: a refusal with a review standing is always a
    // continuation, and a refused continuation is counted when the user's next send is refused.
    if (refusal) capture('ai_credits_exhausted', { source: 'chat' });
    return finalizeTerminalizedRun({
      runId,
      currentEditor: getRequestEditor(requestId),
      onTerminalizeWithoutReview: () => publishRefusedRun(runId, refusal),
    });
  };

  return { prepareAttempt, finalizeDocEdit, finalizeRun, cancel, error, refuse };
}

// Captures the run identity at creation so post-await terminal steps can bail out once a run is
// cleared or restarted. The immutable reducer preserves the identity across mid-run record copies,
// so applied UI events do not falsely mark an in-flight terminal stale.
export function createRunGuard(requestId: string, runId: number): RunGuard {
  const identity = getAgentRunState(requestId)?.identity;
  return {
    isStale: () => !isCurrentAgentRunSnapshot(requestId, runId, identity),
  };
}

async function finalizeSuccessfulChatRun({
  requestId,
  runId,
  messages,
  plan,
  runtime,
}: {
  requestId: string;
  runId: number;
  messages: UIMessage[];
  plan: CompletionPlan;
  runtime?: Pick<
    AgentLifecycleRuntime,
    'document' | 'persistDocumentPatch' | 'autoNameIfNeeded' | 'onEditPhaseFinish' | 'onFinish'
  >;
}): Promise<void> {
  const runGuard = createRunGuard(requestId, runId);
  const finishedEditor = getRequestEditor(requestId);
  const completionDocument = runtime?.document;
  const runState = getAgentRunState(requestId);
  const awaitingPostEditSummary = isAgentRunAwaitingPostEditSummary(runState);
  const resolution = await resolveCompletionPlan(requestId, runId, plan);
  if (runGuard.isStale()) {
    return;
  }
  const hasEdit = resolution.outcome !== 'completed';
  const hasSetDocumentName = hasSetDocumentNameInCurrentTurn(messages);

  endEditorStreamingPreview(requestId, finishedEditor);

  // Captured before the spine evicts (and possibly destroys) the cached editor, so naming survives
  // a run that finishes after the document was unmounted.
  const namingContent =
    hasEdit && isEditorAlive(finishedEditor) && !finishedEditor.isEmpty ? getEditorHTML(finishedEditor) : undefined;

  const finishResult = await finalizeSuccessfulAgentRun({
    requestId,
    runId,
    runGuard,
    editor: finishedEditor,
    document: completionDocument,
    resolution,
    awaitingPostEditSummary,
    hasEdit,
    persistCompletionPatch: () =>
      persistCompletionDocumentPatch({
        patch: buildCompletionDocumentPatch({
          requestId,
          document: completionDocument,
          editor: finishedEditor,
          messages,
          outcome: resolution.outcome,
        }),
        persistDocumentPatch: runtime?.persistDocumentPatch,
      }).then(() => undefined),
    capture: {
      outputCharCount: getEditorCharCount(finishedEditor),
      promptInputCharCount: getLastUserPromptCharCount(messages),
      targetedEdit: getAgentRunEditScope(requestId) === 'targeted',
      streamingEnabled: getAgentRunStreamingEnabled(requestId),
      usage: getLastAssistantUsage(messages),
    },
    onEditPhaseFinish: runtime?.onEditPhaseFinish,
    onFinish: runtime?.onFinish,
  });

  if (!finishResult) {
    return;
  }

  if (hasSetDocumentName || !namingContent || runGuard.isStale() || !runtime?.autoNameIfNeeded) {
    emitAgentRunFinished(finishResult);
    return;
  }

  // Naming is not awaited, so neither run completion nor the broadcast is delayed by it. The
  // broadcast instead carries a pending name that resolves with the document's latest name once
  // naming settles, so subscribers can show the generated name instead of the default one.
  const naming = runtime.autoNameIfNeeded({
    document: completionDocument,
    content: namingContent,
    messages,
    isStale: runGuard.isStale,
  });
  emitAgentRunFinished(
    finishResult,
    naming.then(() => completionDocument?.getLatest().name)
  );
}

// Shared success terminal for both the chat path (via finalizeSuccessfulChatRun) and Generate Text.
// Callers claim terminalization, resolve the outcome, and pass the outcome-specific persist step;
// this spine owns the identical ordering from persist through cache eviction. It returns the finish
// result (undefined when the run went stale) and leaves the global finish broadcast to the caller,
// which emits it right away; the chat path adds a pending document name when it starts fallback
// auto-naming.
export async function finalizeSuccessfulAgentRun({
  requestId,
  runId,
  runGuard,
  editor,
  document,
  resolution,
  awaitingPostEditSummary,
  hasEdit,
  persistCompletionPatch,
  capture,
  onEditPhaseFinish,
  onFinish,
}: SuccessfulAgentRunFinalization): Promise<AgentFinishResult | undefined> {
  await persistCompletionPatch();
  if (runGuard.isStale()) {
    return undefined;
  }

  // Content is durable once the persist resolves; clear the edit snapshot before any later step
  // that can throw, so a subsequent error terminal cannot revert persisted content.
  clearAgentRunEditSnapshot(requestId, runId);

  const documentType = getAgentDocumentType({ document, editor });

  if (!awaitingPostEditSummary && resolution.outcome !== 'completed') {
    publishEditPhaseOutcome(requestId, runId, resolution);
    onEditPhaseFinish?.({
      hasEdit,
      requestId,
      documentName: document?.getLatest().name,
      ...(documentType ? { documentType } : {}),
    });
  }

  publishAgentRunEvent(requestId, runId, { type: 'completed' });

  captureNoteGeneration({
    requestId,
    document,
    outputCharCount: capture.outputCharCount,
    promptInputCharCount: capture.promptInputCharCount,
    targetedEdit: capture.targetedEdit,
    streamingEnabled: capture.streamingEnabled,
    usage: capture.usage,
  });

  // The captured document is the revision the session was bound with; a rename during the run (set
  // name tool, manual rename) only shows on the latest revision.
  const finishResult = {
    hasEdit,
    requestId,
    documentName: document?.getLatest().name,
    ...(documentType ? { documentType } : {}),
  };

  onFinish?.(finishResult);
  evictCachedEditor(requestId);
  return finishResult;
}

async function finalizeSuccessfulAgentEditPhase({
  requestId,
  runId,
  messages,
  plan,
  finishedEditor,
  document,
  persistDocumentPatch,
  onEditPhaseFinish,
}: {
  requestId: string;
  runId: number;
  messages: UIMessage[];
  plan: CompletionPlan;
  finishedEditor: Editor;
  document?: PersistedDocument;
  persistDocumentPatch?: PersistedDocumentWriter;
  onEditPhaseFinish?: (result?: AgentFinishResult) => void;
}): Promise<boolean> {
  const runStateBeforeFinalize = getAgentRunState(requestId);
  const shouldEmitEditPhaseFinish =
    !isAgentRunAwaitingPostEditSummary(runStateBeforeFinalize) && deriveEditorState(runStateBeforeFinalize) !== 'review';

  const runGuard = createRunGuard(requestId, runId);

  const resolution = await resolveCompletionPlan(requestId, runId, plan);

  if (runGuard.isStale()) return false;
  if (resolution.outcome === 'completed' || hasAgentRunClaimedTerminalization(requestId)) return false;

  if (resolution.outcome === 'direct_write') {
    const completionDocumentPatch = buildCompletionDocumentPatch({
      requestId,
      document,
      editor: finishedEditor,
      messages,
      outcome: resolution.outcome,
    });
    const durableEdit = { doc: finishedEditor.state.doc, toolCallIds: getCurrentTurnEditorWriteToolCallIds(messages) };
    // Content counts as durable from the moment its persist starts, so a terminal that claims
    // mid-persist does not revert it. A persist that does not land puts the snapshot back.
    const revertData = getAgentRunRevertData(requestId);
    const startedFromEmptyEditor = getAgentRunStartedFromEmptyEditor(requestId);
    const previousDurableEdit = getAgentRunDurableEdit(requestId);
    const restoreEditSnapshot = () => {
      if (runGuard.isStale()) return;
      setAgentRunRevertData(requestId, revertData);
      setAgentRunStartedFromEmptyEditor(requestId, startedFromEmptyEditor);
      setAgentRunDurableEdit(requestId, runId, previousDurableEdit);
    };
    clearAgentRunEditSnapshot(requestId, runId);
    setAgentRunDurableEdit(requestId, runId, durableEdit);
    const didPersistCompletionPatch = await persistCompletionDocumentPatch({
      patch: completionDocumentPatch,
      persistDocumentPatch,
    }).catch((error: unknown) => {
      restoreEditSnapshot();
      throw error;
    });
    if (!didPersistCompletionPatch) restoreEditSnapshot();
    if (runGuard.isStale()) return false;
    if (hasAgentRunClaimedTerminalization(requestId)) return false;
  }

  endEditorStreamingPreview(requestId, finishedEditor);
  publishEditPhaseOutcome(requestId, runId, resolution);
  if (shouldEmitEditPhaseFinish) {
    const documentType = getAgentDocumentType({ document, editor: finishedEditor });
    onEditPhaseFinish?.({
      hasEdit: true,
      requestId,
      documentName: document?.getLatest().name,
      ...(documentType ? { documentType } : {}),
    });
  }

  return true;
}

// Decides, synchronously, what a successful completion persists, and refuses (throws
// ContentTooLargeError) an edit whose saved record would be too large: the edited document of a
// direct write, or of a write that leaves the content an earlier edit phase persisted, must fit
// TEXT_BUDGET, and a review must pass `prepareReview`.
function planCompletion({
  requestId,
  messages,
  editorState,
  editor,
  persistDocumentPatch,
}: CompletionPlanOptions): CompletionPlan {
  const inspection = inspectCompletionOutcome({ requestId, messages, editorState });
  const currentEditor = getRequestEditor(requestId, editor);

  if (inspection.outcome === 'needs_review_resolution') {
    const pendingReview =
      currentEditor && persistDocumentPatch
        ? prepareReview({ editor: currentEditor, beforeDoc: inspection.revertData.beforeDoc })
        : null;
    return pendingReview && persistDocumentPatch
      ? { outcome: 'commit_review', pendingReview, persistDocumentPatch }
      : { outcome: 'write_finished' };
  }

  const durableDoc = getAgentRunDurableEdit(requestId)?.doc;
  const savesEditorContent =
    inspection.outcome === 'direct_write' ||
    (inspection.outcome === 'write_finished' && !!durableDoc && !currentEditor?.state.doc.eq(durableDoc));
  if (currentEditor && savesEditorContent) {
    assertDocumentFitsTextBudget(currentEditor.state.doc, getEditorHTML(currentEditor));
  }

  return { outcome: inspection.outcome };
}

function resolveCompletionPlan(requestId: string, runId: number, plan: CompletionPlan): Promise<CompletionResolution> {
  if (plan.outcome !== 'commit_review') {
    return Promise.resolve({ outcome: plan.outcome });
  }

  return commitReviewOutcome({
    requestId,
    runId,
    pendingReview: plan.pendingReview,
    persistDocumentPatch: plan.persistDocumentPatch,
  });
}

function inspectCompletionOutcome({
  requestId,
  messages,
  editorState,
}: Pick<CompletionPlanOptions, 'requestId' | 'messages' | 'editorState'>): CompletionOutcomeInspection {
  const { hasEdit, startedFromEmptyEditor, revertData } = getCompletionInspection(requestId, messages);

  if (!hasEdit) {
    return { outcome: 'completed' };
  }

  if (isAwaitingPostEditSummary(requestId)) {
    return {
      outcome: editorState === 'review' ? 'review_ready' : startedFromEmptyEditor ? 'direct_write' : 'write_finished',
    };
  }

  if (startedFromEmptyEditor) {
    return { outcome: 'direct_write' };
  }

  return revertData ? { outcome: 'needs_review_resolution', revertData } : { outcome: 'write_finished' };
}

function getCompletionInspection(
  requestId: string,
  messages: UIMessage[]
): {
  hasEdit: boolean;
  startedFromEmptyEditor: boolean;
  revertData: RevertData | null;
} {
  return {
    hasEdit: hasCurrentTurnEditorWrite(messages),
    startedFromEmptyEditor: getAgentRunStartedFromEmptyEditor(requestId),
    revertData: getAgentRunRevertData(requestId),
  };
}

function hasCurrentTurnEditorWrite(messages: UIMessage[]): boolean {
  return getCurrentTurnAssistantMessages(messages).some((message) =>
    getToolParts(message).some((part) => isEditorWriteToolType(part.type))
  );
}

function getLastUserPromptCharCount(messages?: UIMessage[]): number {
  const lastUserMessage = messages?.findLast((message) => message.role === 'user');
  return lastUserMessage ? getDisplayText(lastUserMessage).length : 0;
}

function getLastAssistantUsage(messages: UIMessage[]): GenerationUsageMessageMetadata | undefined {
  return messages.findLast((message) => message.role === 'assistant')?.metadata as GenerationUsageMessageMetadata | undefined;
}

function isAwaitingPostEditSummary(requestId: string): boolean {
  return isAgentRunAwaitingPostEditSummary(getAgentRunState(requestId));
}

// The review a run's edit persists, or null when there is nothing to review. Throws
// ContentTooLargeError when the review is too large to save.
export function prepareReview({
  editor,
  beforeDoc,
  configOptions,
}: {
  editor: Editor;
  beforeDoc: Node;
  configOptions?: {
    showReplacement?: boolean;
  };
}): PendingReview | null {
  if (editor.state.doc.eq(beforeDoc)) {
    return null;
  }

  const pendingReview = createPendingReview({ editor, beforeDoc, config: buildSuggestionConfig(beforeDoc, configOptions) });
  if (pendingReview) {
    assertReviewFitsContentBudget({ editor, beforeDoc, pendingReview });
  }
  return pendingReview;
}

export function commitReviewOutcome({
  requestId,
  runId,
  pendingReview,
  persistDocumentPatch,
}: {
  requestId: string;
  runId: number;
  pendingReview: PendingReview;
  persistDocumentPatch: PersistedDocumentWriter;
}): Promise<CompletionResolution> {
  return trackPendingReviewCommit(
    requestId,
    runId,
    (async () => {
      try {
        await persistDocumentPatch({ pendingReview });
      } catch (error) {
        console.error('Failed to persist pendingReview:', error);
        throw error;
      }
      return {
        outcome: 'review_ready',
      };
    })()
  );
}

function buildSuggestionConfig(
  beforeDoc: Node,
  options?: {
    showReplacement?: boolean;
  }
): SuggestionDiffConfig {
  const showReplacement = options?.showReplacement ?? true;

  return {
    doc: beforeDoc,
    reviewOptions: {
      diffUtilityOptions: {
        mode: 'smart' as const,
      },
      displayOptions: {
        showReplacement,
        ...(!showReplacement
          ? {
              attributes: { class: 'hide-diff' },
            }
          : {}),
      },
    },
  };
}

function trackPendingReviewCommit(
  requestId: string,
  runId: number,
  commit: Promise<CompletionResolution>
): Promise<CompletionResolution> {
  const entry: PendingReviewCommit = { runId, commit };
  setAgentRunPendingReviewCommit(requestId, entry);
  const cleanupPendingCommit = () => clearAgentRunPendingReviewCommit(requestId, entry);
  void commit.then(cleanupPendingCommit, cleanupPendingCommit);
  return commit;
}

async function persistCompletionDocumentPatch({
  patch,
  persistDocumentPatch,
}: {
  patch: ReturnType<typeof buildCompletionDocumentPatch>;
  persistDocumentPatch?: PersistedDocumentWriter;
}): Promise<boolean> {
  if (!patch || !persistDocumentPatch) {
    return false;
  }

  await persistDocumentPatch(patch);
  return true;
}

function publishEditPhaseOutcome(
  requestId: string,
  runId: number,
  resolution: Exclude<CompletionResolution, { outcome: 'completed' }>
): void {
  publishAgentRunEvent(
    requestId,
    runId,
    resolution.outcome === 'review_ready' ? { type: 'review_ready' } : { type: 'agent_write_finished' }
  );
}

async function waitForPendingReviewCommit(requestId: string, runId: number): Promise<PendingReviewCommitResult> {
  const pendingCommit = getPendingReviewCommit(requestId, runId);
  if (!pendingCommit) {
    return { status: 'none' };
  }

  try {
    const resolution = await pendingCommit;
    return resolution.outcome === 'review_ready' ? { status: 'persisted' } : { status: 'none' };
  } catch (error) {
    return { status: 'failed', error };
  }
}

function getPendingReviewCommit(requestId: string, runId: number): PendingReviewCommit['commit'] | null {
  const pendingCommit = getAgentRunPendingReviewCommit(requestId);
  if (!pendingCommit || pendingCommit.runId !== runId) {
    return null;
  }

  return pendingCommit.commit;
}
